/**
 * Helpers that join the AI chat and the document import.
 *
 * The ride page has one AI assistant: files dropped on the chat go through the
 * document-import pipeline, and the chat only hears about them once they are
 * on the ride. These are the parts of that hand-off with rules worth testing;
 * the panels just call them.
 */

export const ACCEPTED_DOCUMENT_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];

// The same ceiling the documents panel has always applied (5 - activeCount):
// more staged cards than this is more than a manager reviews in one go.
export const MAX_ACTIVE_DOCUMENTS = 5;

function extensionOf(name) {
  const dot = (name || '').lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

// Splits what the manager dropped or picked into what gets uploaded and what
// does not. A file cut by the 5-document ceiling is counted in overLimit, not
// listed as rejected: its type was fine, there was just no room for it.
export function pickDocumentFiles(files, activeCount) {
  const supported = [];
  const rejected = [];
  (files || []).forEach((file) => {
    if (ACCEPTED_DOCUMENT_EXTENSIONS.includes(extensionOf(file && file.name))) supported.push(file);
    else rejected.push((file && file.name) || '');
  });
  const room = Math.max(0, MAX_ACTIVE_DOCUMENTS - (activeCount || 0));
  return {
    accepted: supported.slice(0, room),
    rejected,
    overLimit: Math.max(0, supported.length - room),
  };
}

function joinPresent(parts, separator) {
  return parts.filter(part => part !== undefined && part !== null && String(part).trim() !== '').join(separator);
}

function describeOne(result) {
  const type = (result && result.type) || 'document';
  let details = '';
  if (type === 'accommodation') {
    const data = (result.extracted && result.extracted.data) || {};
    const dates = data.check_in_date && data.check_out_date
      ? `${data.check_in_date} to ${data.check_out_date}`
      : '';
    details = joinPresent([data.name, dates], ', ');
  } else {
    details = joinPresent((result.points || []).map(point => joinPresent([
      point && (point.label || point.labelSuffix),
      point && point.date,
      point && point.time,
    ], ' ')), '; ');
  }
  return details ? `- ${type}: ${details}` : '';
}

// The note the model gets after documents are added to the ride, so the next
// question ("what's near my hotel?") has the context. A model instruction,
// never shown to the manager, so it stays in English like the auto-continue
// turns in the aiChat store.
export function describeAttachedDocuments(results) {
  const lines = (results || []).map(describeOne).filter(Boolean);
  if (!lines.length) return '';
  return [
    'The manager just added these booking documents to the ride. They are already saved; do not propose adding them again.',
    ...lines,
  ].join('\n');
}

// The history sent with each chat turn. The "files I dropped" bubbles are UI
// only -- an empty user turn would confuse the model -- so they are dropped
// before the window is cut, which keeps the window full of real turns.
export function buildChatHistory(messages, limit = 10) {
  return (messages || [])
    .filter(message => message.kind !== 'documents')
    .slice(-limit)
    .map(message => ({ role: message.role, content: message.content }));
}
